import React, { useCallback, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import ButtonIcon from '~/components/ButtonIcon';
import Facebook from '~/components/CustomIcons/Facebook';
import Instagram from '~/components/CustomIcons/Instagram';
import Twitter from '~/components/CustomIcons/Twitter';
import YouTube from '~/components/CustomIcons/YouTube';
import ZendeskChatBubble from '~/components/CustomIcons/ZendeskChatBubble';
import FeedbackBlock from '~/components/FeedbackBlock';
import LinkBody from '~/components/LinkBody';
import Groups from '~/components/MaterialIcons/Groups';
import Text from '~/components/Text';
import TextTooltip from '~/components/TextTooltip';
import Tooltip from '~/components/Tooltip';
import { Translation } from '~/components/Translation';
import { Header } from '~/components/Marketing';
import Zendesk from '~/components/Zendesk';
import {
	STREAMYARD_FACEBOOK_GROUP_URL,
	STREAMYARD_FACEBOOK_PAGE_URL,
	STREAMYARD_INSTAGRAM_PAGE_URL,
	STREAMYARD_TWITTER_PAGE_URL,
	STREAMYARD_YOUTUBE_PAGE_URL,
} from '~/constants';
import usePageTrack from '~/services/analytics/usePageTrack';
import analytics from '~/services/analytics';
import { useClearTimeoutRefOnUnmount } from '~/services/utils/hooks';
import { zendeskSelectors, zendeskWidget } from '~/services/zendesk';

import ZendeskContactButton from '~/components/ZendeskContactButton';
import featureFlags from '~/constants/featureFlags';
import Logo from '~/components/Logo';
import fullDuckSvg from './full_duck.svg';

import {
	Content,
	FullDuck,
	Community,
	ContactInfo,
	ChatButton,
	SocialIconRow,
	SocialButton,
} from './Contact.styles';

const socialIcons = [
	{
		href: STREAMYARD_FACEBOOK_GROUP_URL,
		tooltip: <Translation tid="contact.socialIcon.tooltip.facebook_group" />,
		Icon: Groups,
	},
	{
		href: STREAMYARD_FACEBOOK_PAGE_URL,
		tooltip: <Translation tid="contact.socialIcon.tooltip.facebook_page" />,
		Icon: Facebook,
	},
	{
		href: STREAMYARD_YOUTUBE_PAGE_URL,
		Icon: YouTube,
		tooltip: <Translation tid="contact.socialIcon.tooltip.youTube" />,
	},
	{
		href: STREAMYARD_INSTAGRAM_PAGE_URL,
		Icon: Instagram,
		tooltip: <Translation tid="contact.socialIcon.tooltip.instagram" />,
	},
	{
		href: STREAMYARD_TWITTER_PAGE_URL,
		Icon: Twitter,
		tooltip: <Translation tid="contact.socialIcon.tooltip.twitter" />,
	},
];

const Contact = () => {
	const hasZendeskScriptLoaded = useSelector(
		zendeskSelectors.hasZendeskScriptLoaded,
	);
	const isWidgetOpen = useSelector(zendeskSelectors.isWidgetOpen);

	usePageTrack({
		name: analytics.events.CONTACT,
		category: analytics.events.category.APP,
	});
	const chatNotificationTimeout = useRef<NodeJS.Timeout>();
	useClearTimeoutRefOnUnmount(chatNotificationTimeout);

	const [isChatNotificationVisible, setChatNotification] = useState(false);

	const [isHelpOpen, setIsHelpOpen] = useState(false);

	const openHelp = useCallback(() => {
		setIsHelpOpen(true);
		zendeskWidget.open();

		// If the chat is already open, then clicking this won't do anything. Tell the user to use
		// the chat at the bottom of the page.
		if (isWidgetOpen) {
			setChatNotification(true);
			clearTimeout(chatNotificationTimeout.current);
			chatNotificationTimeout.current = setTimeout(() => {
				setChatNotification(false);
			}, 5000);
		}
	}, [isWidgetOpen]);

	const isHelpLoading = isHelpOpen && !hasZendeskScriptLoaded;

	return (
		<main role="main">
			<Zendesk autoOpen={isHelpOpen} />
			<ZendeskContactButton />
			<Header
				title={<Translation tid="contact.support.title" />}
				subtitles={[
					<Translation
						key="1"
						tid="contact.support.welcome"
						components={{
							link1: (
								<LinkBody
									newTab
									to={'https://support.streamyard.com/hc/en-us'}
								/>
							),
							link2: (
								<LinkBody
									newTab
									to={'https://support.streamyard.com/hc/en-us/requests/new'}
								/>
							),
						}}
					/>,
				]}
				cta={
					<Tooltip
						hasArrow={false}
						overlay={
							<FeedbackBlock size="small" variant="primary">
								<Translation tid="contact.support.chat" />
							</FeedbackBlock>
						}
						visible={isChatNotificationVisible}
						placement="right"
					>
						<ChatButton
							// Note that we added this button because simply telling users to
							// use the chat widget at the bottom of the page didn't work well
							// for non-English speakers. They often ended up clicking the
							// feature request link to ask for help since it was the first
							// clickable link on the page.
							disabled={isHelpLoading}
							loading={isHelpLoading}
							onClick={openHelp}
						>
							<ButtonIcon>
								<ZendeskChatBubble />
							</ButtonIcon>
							<Translation tid="contact.support.help" />
						</ChatButton>
					</Tooltip>
				}
			/>
			<Content>
				<Community>
					<Text variant="h5" component="h2" gutterBottom>
						<Translation tid="contact.community.title" />
					</Text>
					<Text variant="body" gutterBottom>
						<Translation tid="contact.community.instructions" />
					</Text>
					<SocialIconRow>
						{socialIcons.map(({ href, Icon, tooltip }) => (
							<TextTooltip
								key={href}
								openDelay={0}
								placement="top"
								text={tooltip}
							>
								<SocialButton href={href} newTab>
									<Icon />
								</SocialButton>
							</TextTooltip>
						))}
					</SocialIconRow>
				</Community>
				<ContactInfo className="row">
					<div>
						{featureFlags.orion.logoSwapEnabled ? (
							<Logo
								variant="icon"
								isInverted={false}
								style={{ width: '100px', height: '100px', marginRight: '36px' }}
							/>
						) : (
							<TextTooltip placement="right" text="Quack!">
								<FullDuck alt="StreamYard duck mascot" src={fullDuckSvg} />
							</TextTooltip>
						)}
					</div>
					<div>
						<Text variant="h5" component="h2" gutterBottom>
							<Translation tid="contact.information.title" />
						</Text>
						<Text variant="body2" color="light">
							Bending Spoons US Inc.
						</Text>
						<Text variant="body2" color="light">
							169 Madison Ave, Ste 11218
						</Text>
						<Text variant="body2" color="light" gutterBottom>
							New York, NY 10016
						</Text>
					</div>
				</ContactInfo>
			</Content>
		</main>
	);
};

export default Contact;
